import { QueryClient } from '@tanstack/react-query';

import { ApplicationSource, USER_ROLES } from '../../common/constants';
import { ApplicationDetails } from '../../common/interfaces/entities';
import { ReactQueryKeys } from '../../common/react-query-keys';
import { GlintsChatChannel } from './types/channel';
import { GlintsChatExchangeRequestStatus } from './types/exchange-request';
import {
  GlintsChatMessage,
  GlintsChatMessageContentType,
} from './types/message';

const CANDIDATE_REPLY_SOURCES: ApplicationSource[] = [
  ApplicationSource.RECOMMENDED_TALENT,
  ApplicationSource.TALENT_SEARCH_WEB,
];

export const tracksCandidateReply = (source?: ApplicationSource) =>
  source !== undefined && CANDIDATE_REPLY_SOURCES.includes(source);

export const isCandidateReplyMessage = (message: GlintsChatMessage) =>
  message.sender.loginRole === USER_ROLES.CANDIDATE &&
  message.contentType !== GlintsChatMessageContentType.SYSTEM_NOTIFICATION;

export const hasCompletedCvShare = (
  channel: Pick<GlintsChatChannel, 'exchangeRequests'>
) => {
  const cv = channel.exchangeRequests?.cv;

  return (
    cv?.status === GlintsChatExchangeRequestStatus.DONE && Boolean(cv.file)
  );
};

// Only refresh when the channel can record the reply and the cached
// application details do not have it yet, so this stays a one-time refetch.
export const shouldRefreshApplicationDetails = (
  queryClient: QueryClient,
  channel: Pick<GlintsChatChannel, 'id' | 'source'>
) => {
  if (!tracksCandidateReply(channel.source)) {
    return false;
  }

  const applicationDetails = queryClient.getQueryData<ApplicationDetails>(
    ReactQueryKeys.applicationDetails(channel.id)
  );

  return Boolean(
    applicationDetails &&
      !applicationDetails.employerMetadata?.firstCandidateRepliedAt
  );
};

// WS payloads from chat-connection may omit firstCandidateRepliedAt; keep the
// value already fetched from REST instead of wiping it.
export const withPreservedCandidateReply = (
  channel: GlintsChatChannel,
  previousChannel?: GlintsChatChannel | null
): GlintsChatChannel =>
  channel.firstCandidateRepliedAt === undefined &&
  previousChannel?.firstCandidateRepliedAt
    ? {
        ...channel,
        firstCandidateRepliedAt: previousChannel.firstCandidateRepliedAt,
      }
    : channel;
